/* Wander Painting - the coupon sheet.

   Opening and closing, claiming a code, and copying it. Event delegation from
   the document, so any number of these on a page work and a section swapped in
   by the theme editor needs no re-initialising.

   <dialog> does the heavy lifting: showModal() puts the sheet in the top layer
   so the offer panel's own `overflow: hidden` cannot clip it, and Esc and
   focus trapping come for free. Browsers without it get a positioned
   fallback. */
(function () {
  'use strict';

  function sheetFor(id) {
    return document.getElementById('wp-cpn-' + id);
  }

  function open(sheet) {
    if (!sheet) return;
    if (typeof sheet.showModal === 'function') {
      sheet.showModal();
    } else {
      // No <dialog> support: position it, show it, and stop the page behind
      // it scrolling under the reader's thumb.
      sheet.setAttribute('data-wp-cpn-fallback', '');
      sheet.setAttribute('open', '');
      document.documentElement.style.overflow = 'hidden';
    }
  }

  function close(sheet) {
    if (!sheet) return;

    if (typeof sheet.close !== 'function' || sheet.hasAttribute('data-wp-cpn-fallback')) {
      sheet.removeAttribute('open');
      document.documentElement.style.overflow = '';
      return;
    }

    // dialog.close() is immediate, so an exit animation needs the close held
    // back until it has run. Without this the sheet enters gracefully and then
    // vanishes, which reads worse than having no animation at all.
    if (sheet.hasAttribute('data-closing')) return;

    var reduced = window.matchMedia
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced) {
      sheet.close();
      return;
    }

    sheet.setAttribute('data-closing', '');
    var done = false;
    var finish = function () {
      if (done) return;
      done = true;
      sheet.removeAttribute('data-closing');
      sheet.close();
    };
    sheet.addEventListener('animationend', finish, { once: true });
    // A safety net: if the animation never fires - no CSS, a browser that
    // skips it - the sheet still closes rather than sticking open.
    window.setTimeout(finish, 320);
  }

  function claim(button) {
    var action = button.parentNode;
    var got = action.querySelector('.wp-cpn-card__got');
    if (!got) return;
    button.hidden = true;
    got.hidden = false;
    // Move focus onto the copy button: the thing the reader wants next is the
    // code, and the button they just pressed has gone.
    var copy = got.querySelector('[data-wp-cpn-copy]');
    if (copy) copy.focus();
  }

  // Select the code element's own contents and copy that.
  //
  // Not a hidden textarea appended to <body>: showModal() makes everything
  // outside the dialog inert, so a field parked on the body cannot be selected
  // and execCommand quietly returns false. The <code> element is inside the
  // dialog, so it can be, and selecting it leaves the code highlighted - which
  // is the right state to be left in if the copy is refused anyway.
  function copyFromElement(el) {
    var range = document.createRange();
    range.selectNodeContents(el);
    var sel = window.getSelection();
    sel.removeAllRanges();
    sel.addRange(range);
    var ok = false;
    try { ok = document.execCommand('copy'); } catch (e) { ok = false; }
    return ok;
  }

  function copyText(text, el) {
    // In-app browsers routinely refuse the async clipboard API, and this page
    // is mostly read inside Facebook and Instagram, so the fallback matters
    // as much as the primary path.
    if (navigator.clipboard && navigator.clipboard.writeText) {
      return navigator.clipboard.writeText(text).catch(function () {
        if (copyFromElement(el)) return;
        throw new Error('copy refused');
      });
    }
    return copyFromElement(el)
      ? Promise.resolve()
      : Promise.reject(new Error('copy refused'));
  }

  function onCopy(button) {
    var card = button.closest('.wp-cpn-card');
    var code = card && card.querySelector('[data-wp-cpn-code]');
    if (!code) return;
    var text = code.textContent.trim();
    var original = button.getAttribute('data-wp-cpn-label') || button.textContent.trim();
    button.setAttribute('data-wp-cpn-label', original);

    copyText(text, code).then(function () {
      button.textContent = 'Copied';
      button.setAttribute('data-copied', '');
    }).catch(function () {
      // Refused outright. Saying so is better than a button that looks like it
      // worked; the code is selected and on screen, ready to be copied by hand.
      button.textContent = 'Copy it from above';
      copyFromElement(code);
    }).then(function () {
      window.setTimeout(function () {
        button.textContent = original;
        button.removeAttribute('data-copied');
      }, 2400);
    });
  }

  /* ---------------------------------------------------------- countdown
     Counts down to a real deadline - the end time set on the discount in
     Shopify - and when it passes, retires the coupon: the card greys out,
     the claim button becomes "Ended", and the code is not handed out any
     more. That last part is why the deadline has to be real. A timer that
     resets itself would be announcing an expiry that never happens and then
     printing "Ended" on a code that still works, which anyone who waited and
     tried it would catch.

     Times come from Liquid as ISO strings and are compared against the
     reader's own clock, so a phone with the wrong time is the only thing
     that can disagree. */

  function parseWhen(value) {
    if (!value) return null;
    var t = Date.parse(value);
    return isNaN(t) ? null : t;
  }

  function formatGap(ms) {
    var total = Math.max(0, Math.floor(ms / 1000));
    var days = Math.floor(total / 86400);
    var hours = Math.floor((total % 86400) / 3600);
    var mins = Math.floor((total % 3600) / 60);
    var secs = total % 60;
    var pad = function (n) { return (n < 10 ? '0' : '') + n; };
    // Days only when there are some; otherwise a clock, which is what makes a
    // short deadline feel short.
    if (days > 0) return days + 'd ' + pad(hours) + ':' + pad(mins) + ':' + pad(secs);
    if (hours > 0) return pad(hours) + ':' + pad(mins) + ':' + pad(secs);
    return pad(mins) + ':' + pad(secs);
  }

  // Reversible, because a looping window has to come back. Everything it
  // touches is rendered in the markup and toggled, rather than replaced.
  function setExpired(card, expired) {
    if (!card) return;
    if (card.classList.contains('wp-cpn-card--ended') === expired) return;
    card.classList.toggle('wp-cpn-card--ended', expired);

    var sheet = card.closest('[data-wp-cpn]');
    var claim = card.querySelector('[data-wp-cpn-claim]');
    var gone = card.querySelector('[data-wp-cpn-gone]');
    var got = card.querySelector('.wp-cpn-card__got');
    var claimed = got && !got.hidden;

    if (expired) {
      // The code goes away with the offer. Leaving it on screen would be
      // handing out something the card has just said has ended.
      if (got) got.hidden = true;
      if (claim) claim.hidden = true;
      if (gone) gone.hidden = false;
    } else {
      if (gone) gone.hidden = true;
      // Someone who had already claimed gets their code back rather than
      // being made to claim it twice.
      if (claimed && got) got.hidden = false;
      else if (claim) claim.hidden = false;
    }

    // Greying the card is not enough while the words on it still say it is
    // live: the tag and, if nothing in the group is live, its heading.
    var tag = card.querySelector('.wp-cpn-card__tag');
    if (tag && sheet) {
      if (expired) {
        if (!tag.hasAttribute('data-wp-cpn-live-tag')) {
          tag.setAttribute('data-wp-cpn-live-tag', tag.textContent.trim());
        }
        tag.textContent = sheet.getAttribute('data-wp-cpn-ended-tag') || 'Ended';
      } else if (tag.hasAttribute('data-wp-cpn-live-tag')) {
        tag.textContent = tag.getAttribute('data-wp-cpn-live-tag');
      }
    }

    var list = card.closest('.wp-cpn__list');
    var heading = list && list.previousElementSibling;
    if (!sheet || !heading || !heading.classList.contains('wp-cpn__group')) return;
    if (!heading.hasAttribute('data-wp-cpn-live-group')) {
      heading.setAttribute('data-wp-cpn-live-group', heading.textContent.trim());
    }
    var stillLive = list.querySelectorAll('.wp-cpn-card:not(.wp-cpn-card--ended)').length;
    heading.textContent = stillLive
      ? heading.getAttribute('data-wp-cpn-live-group')
      : (sheet.getAttribute('data-wp-cpn-ended-group') || 'Recently ended');
  }

  /* Where an element is in a repeating window.

     Anchored to the wall clock - now modulo the cycle - rather than to when
     the visitor arrived. Two things fall out of that, and both matter: the
     two coupon bars on a page always agree with each other, and reloading
     does not snap the clock back to full, which is the giveaway that makes a
     repeating timer look broken. */
  function loopState(el, now) {
    var liveMs = parseFloat(el.getAttribute('data-wp-cpn-loop')) * 60000;
    if (!liveMs || liveMs <= 0) return null;
    var gapMs = (parseFloat(el.getAttribute('data-wp-cpn-loop-gap')) || 0) * 60000;
    var cycle = liveMs + gapMs;
    var phase = now % cycle;
    return phase < liveMs
      ? { live: true, remaining: liveMs - phase }
      : { live: false, remaining: cycle - phase };
  }

  function paintCounter(el, live, remaining) {
    var liveEl = el.querySelector('.wp-cpn-count__live');
    var overEl = el.querySelector('.wp-cpn-count__over');
    if (liveEl) liveEl.hidden = !live;
    if (overEl) overEl.hidden = live;
    if (live) {
      el.removeAttribute('data-wp-cpn-over');
      var clock = el.querySelector('[data-wp-cpn-clock]');
      if (clock) clock.textContent = formatGap(remaining);
    } else {
      el.setAttribute('data-wp-cpn-over', '');
    }
  }

  function tick() {
    var now = Date.now();
    var i;

    // Fixed deadlines.
    var fixed = document.querySelectorAll('[data-wp-cpn-until]');
    for (i = 0; i < fixed.length; i++) {
      var until = parseWhen(fixed[i].getAttribute('data-wp-cpn-until'));
      if (until === null) continue;
      paintCounter(fixed[i], until > now, until - now);
    }

    var expiring = document.querySelectorAll('[data-wp-cpn-expires]');
    for (i = 0; i < expiring.length; i++) {
      var ends = parseWhen(expiring[i].getAttribute('data-wp-cpn-expires'));
      if (ends !== null) setExpired(expiring[i], ends <= now);
    }

    // Repeating windows.
    var looping = document.querySelectorAll('[data-wp-cpn-loop]');
    for (i = 0; i < looping.length; i++) {
      var el = looping[i];
      var state = loopState(el, now);
      if (!state) continue;
      if (el.classList.contains('wp-cpn-card')) {
        setExpired(el, !state.live);
      } else {
        paintCounter(el, state.live, state.remaining);
      }
    }

  }

  // Started unconditionally. This script is only ever loaded by the coupon
  // snippet, so if it is running there is a drawer on the page; and checking
  // for a countdown once at load would miss a section swapped in by the theme
  // editor. tick() costs two querySelectorAll on a small DOM and does nothing
  // when there is nothing to count.
  tick();
  window.setInterval(tick, 1000);

  document.addEventListener('click', function (event) {
    var opener = event.target.closest('[data-wp-cpn-open]');
    if (opener) {
      open(sheetFor(opener.getAttribute('data-wp-cpn-open')));
      return;
    }

    if (event.target.closest('[data-wp-cpn-close]')) {
      close(event.target.closest('[data-wp-cpn]'));
      return;
    }

    var claimer = event.target.closest('[data-wp-cpn-claim]');
    if (claimer) {
      claim(claimer);
      return;
    }

    var copier = event.target.closest('[data-wp-cpn-copy]');
    if (copier) {
      onCopy(copier);
      return;
    }

    // A click on the dialog itself, rather than on the sheet inside it, is a
    // click on the backdrop.
    var sheet = event.target.closest('[data-wp-cpn]');
    if (sheet && !event.target.closest('.wp-cpn__sheet')) close(sheet);
  });

  // The fallback path has no Esc handling of its own.
  document.addEventListener('keydown', function (event) {
    if (event.key !== 'Escape') return;
    var openFallback = document.querySelector('[data-wp-cpn][data-wp-cpn-fallback][open]');
    if (openFallback) close(openFallback);
  });
})();
